import * as Sentry from "@sentry/react";

// Shortest valid WAV (no samples). Playing it only serves to register a media
// play() inside a user gesture.
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

// iOS 27 Safari no longer lets a remote WebRTC <audio> element autoplay (or
// play() outside a gesture) while the page is capturing camera/mic; play()
// rejects with NotAllowedError. Calling play() on any media element inside a
// user gesture unlocks later playback for the whole document.
//
// Must be called synchronously from a click/touch handler.
let unlockElement: HTMLAudioElement | null = null;

export function unlockAudioPlayback() {
  try {
    if (!unlockElement) {
      unlockElement = document.createElement("audio");
      unlockElement.setAttribute("playsinline", "");
      unlockElement.style.display = "none";
      unlockElement.src = SILENT_WAV;
      document.body.appendChild(unlockElement);
    }
    // The promise may never settle for an empty clip; the unlock happens on the call.
    unlockElement.play().catch(() => {});
  } catch {}
}

const blockedElements = new Set<HTMLMediaElement>();
let isGestureListenerAdded = false;

function retryBlockedOnGesture() {
  blockedElements.forEach((el) => {
    el.play()
      .then(() => {
        blockedElements.delete(el);
        Sentry.captureMessage("Remote audio playback resumed after user gesture");
      })
      .catch(() => {});
  });
  if (blockedElements.size === 0) {
    document.removeEventListener("touchend", retryBlockedOnGesture, true);
    document.removeEventListener("click", retryBlockedOnGesture, true);
    isGestureListenerAdded = false;
  }
}

// Starts playback of an attached remote audio element. If the browser blocks
// it, report it and retry on the user's next tap.
export function playRemoteAudio(el: HTMLMediaElement) {
  if (!el.paused) {
    return;
  }
  el.play()
    .then(() => blockedElements.delete(el))
    .catch((error: any) => {
      const track = (el.srcObject as MediaStream | null)?.getAudioTracks()[0];
      Sentry.captureMessage("Remote audio playback blocked", {
        level: "error",
        extra: {
          error: `${error?.name}: ${error?.message}`,
          userAgent: navigator.userAgent,
          trackReadyState: track?.readyState,
          trackMuted: track?.muted,
          trackEnabled: track?.enabled,
          elementReadyState: el.readyState,
        },
      });
      if (error?.name !== "NotAllowedError") {
        return;
      }
      blockedElements.add(el);
      if (!isGestureListenerAdded) {
        document.addEventListener("touchend", retryBlockedOnGesture, true);
        document.addEventListener("click", retryBlockedOnGesture, true);
        isGestureListenerAdded = true;
      }
    });
}

export function forgetRemoteAudio(el: HTMLMediaElement) {
  blockedElements.delete(el);
}
